import type {
  AnyFunction,
  EventTopicDefinition,
  NexusNamespace,
  TargetStore,
} from "./private.types";
/*                                       */
import { createProxy } from "./proxy";
/*                                       */
import { configSync } from "./proxyConfig";
import type { SetOptions, Unset } from "./public.types";
import * as symbols from "./symbols";

export const stores = new WeakMap<AnyFunction, TargetStore>();

const noopDescriptor = { value: symbols.noop, configurable: true, writable: true };

export function getStore(target: AnyFunction): TargetStore {
  return stores.get(target) as never;
}

export function createStore(name: string | symbol): [store: TargetStore, target: AnyFunction] {
  /*                                    */
  const store: TargetStore = { invocations: [], listeners: [], members: {}, path: name } as never;

  /*                 */
  store.ready = new Promise<AnyFunction>((resolve) => {
    store.onReady = resolve;
  });

  /*                                                            */
  /*                                       */
  const target = function () {
    /* */
  } as AnyFunction;

  /*                                              */
  Object.defineProperty(target, "name", { value: name });
  Object.defineProperty(target, "set", noopDescriptor);
  Object.defineProperty(target, "isSet", noopDescriptor);
  Object.defineProperty(target, "on", noopDescriptor);

  /*                */
  stores.set(target, store);
  return [store, target];
}

export function ensurePropStore(
  target: AnyFunction,
  p: string | symbol,
): [propStore: TargetStore, propTarget: AnyFunction] {
  const store = getStore(target);

  if (!store.members[p]) {
    const res = createStore(p);
    res[0].path = `${store.path}.${p.toString()}`;
    /*                                            */
    store.members[p] = res[1];
    /*                                          */
    /*                            */
    /*                                                                                */
    Object.defineProperty(target, p, noopDescriptor);
    return res;
  }

  const propTarget = store.members[p];
  const propStore = getStore(propTarget);

  return [propStore, propTarget];
}

export function getStoreType(store: TargetStore): string {
  return (
    (
      [
        [store.isNs, "Namespace"],
        [store.isEvent, "Event"],
        [store.isFunc, "Function"],
        [store.isSig, "Signal"],
      ] as const
    ).find(([v]) => !!v)?.[1] ?? "NexusWrapper"
  );
}

function updateHandler(
  store: TargetStore,
  target: AnyFunction,
  handler: AnyFunction,
  options?: SetOptions,
): Unset {
  if (store.isEvent || store.isNs || store.isSig) {
    throw Error(
      `Can not set handler to ${
        store.path
      } because is has been already initialized as ${getStoreType(store)}`,
    );
  }

  store.handler = handler as AnyFunction;
  store.isReady = true;
  store.isFunc = true;
  store.onReady(createProxy(target, configSync));

  if (options?.clean) {
    /*                                            */
    store.invocations.length = 0;
  }

  /*                                */
  store.invocations.splice(0).forEach((invocation) => {
    try {
      /*                                              */
      const resultOrPromise = handler.apply(invocation[0], invocation[1]);
      invocation[2](resultOrPromise);
    } catch (e) {
      /*                                        */
      invocation[3](e);
    }
  });

  return () => {
    const res = store.handler === handler;
    if (!res) {
      /*                            */
      return res;
    }

    /*                                 */
    delete store.isFunc;
    delete store.isReady;
    delete store.handler;
    return res;
  };
}

function updateNamespace(
  store: TargetStore,
  target: AnyFunction,
  ns?: NexusNamespace<unknown>,
  options?: SetOptions,
): Unset {
  if (store.isReady) {
    /*                                                                               */
    /*                                                                 */
    /*                                                                          */
    throw Error("Unexpected namespace assignment, namespace has been already initialized");
  }

  if ([store.isFunc || store.isEvent || store.isSig].some(Boolean)) {
    throw Error(
      `Can not assign namespace to ${
        store.path
      } because is has been already initialized as ${getStoreType(store)}`,
    );
  }

  if (ns) {
    Object.entries(ns).forEach(([key, val]) => {
      const [propStore, propTarget] = ensurePropStore(target, key);
      /*                                                               */
      updateStoreValue(propStore, propTarget, val, options);
    });
  }

  store.isNs = true;
  store.isReady = true;
  store.onReady(createProxy(target, configSync));

  return () => {
    const deleted = Object.values(store.members).map((propTarget) => {
      const propStore = getStore(propTarget);
      delete propStore.handler;
      propStore.invocations.splice(0);
      propStore.listeners.splice(0);
      return propTarget;
    });

    delete store.isNs;
    delete store.isReady;
    return deleted.length > 0;
  };
}

function isEventDefinition(value: unknown): value is EventTopicDefinition<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.prototype.hasOwnProperty.call(value, symbols.event)
  );
}

export function addListener(store: TargetStore, listener: AnyFunction): Unset {
  if (typeof listener !== "function") {
    throw Error(`Invalid listener type ${typeof listener} provided. a function is expected`);
  }

  if (store.isFunc || store.isNs || store.isSig) {
    throw Error(
      `Can not add listener to ${
        store.path
      } because is has been already initialized as ${getStoreType(store)}`,
    );
  }

  store.isEvent = true;
  store.listeners.push(listener);

  return () => {
    const deleted = store.listeners.splice(store.listeners.indexOf(listener), 1);

    if (store.listeners.length < 1) {
      delete store.isReady;
      delete store.isEvent;
    }

    return deleted.length > 0;
  };
}

/**
 *
 */
export function updateStoreValue(
  store: TargetStore,
  target: AnyFunction,
  value: unknown,
  options?: SetOptions,
): Unset {
  if (typeof value === "function") {
    return updateHandler(store, target, value as AnyFunction, options);
  }

  if (isEventDefinition(value)) {
    store.isEvent = true;

    /*                                                               */
    const res = value[symbols.event].map((l) => addListener(store, l));
    return () => {
      return res.map((d) => d()).every(Boolean);
    };
  }

  if (
    !value ||
    (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0)
  ) {
    return updateNamespace(store, target, value as NexusNamespace<unknown>, options);
  }

  throw Error(
    `Unexpected Input for: ${
      store.path
    }. Nexus expects input to be either a function, nexus definition, or an object with at least one property, given was: ${typeof value}`,
  );
}
